import { createFieldPopup } from './field-popup.js';

const domains = ['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'icloud.com', 'aol.com', 'proton.me', 'live.com'];
let nextId = 0;

/** Optional domain completions. Only an explicit selection changes the address. */
export function createEmailField(input: HTMLInputElement, options: { suggestionsLabel: string }): { dispose(): void } {
  const panel = document.createElement('div');
  panel.className = 'email-suggestions';
  const list = document.createElement('ul');
  list.className = 'field-popup-list';
  list.id = `email-suggestions-${++nextId}`;
  list.setAttribute('role', 'listbox');
  list.setAttribute('aria-label', options.suggestionsLabel);
  panel.append(list);

  const attributes = ['role', 'aria-autocomplete', 'aria-expanded', 'aria-controls', 'aria-activedescendant'];
  const previous = attributes.map(name => input.getAttribute(name));
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-controls', list.id);
  input.setAttribute('aria-expanded', 'false');
  input.removeAttribute('aria-activedescendant');

  let addresses: string[] = [];
  let active = -1;
  let open = false;
  let composing = false;
  let selecting = false;
  let disposed = false;
  const popup = createFieldPopup(input, panel, { onDismiss: close });

  function close(): void {
    open = false;
    active = -1;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    for (const item of list.children) {
      item.removeAttribute('data-active');
      item.setAttribute('aria-selected', 'false');
    }
    popup.hide();
  }

  function enabled(): boolean {
    return !disposed && !input.matches(':disabled') && !input.readOnly && !composing;
  }

  function refresh(): void {
    if (selecting || disposed) return;
    close();
    if (!enabled() || document.activeElement !== input) return;
    const match = /^([^@\s]+)@([^@\s]*)$/.exec(input.value);
    if (!match) return;
    const prefix = match[2].toLowerCase();
    if (domains.includes(prefix)) return;
    addresses = domains.filter(domain => domain.startsWith(prefix)).map(domain => `${match[1]}@${domain}`);
    list.replaceChildren();
    addresses.forEach((address, index) => {
      const item = document.createElement('li');
      item.className = 'field-popup-option';
      item.id = `${list.id}-${index}`;
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
      item.dataset.index = String(index);
      item.tabIndex = -1;
      item.textContent = address;
      list.append(item);
    });
    if (addresses.length) {
      open = true;
      input.setAttribute('aria-expanded', 'true');
      popup.show();
    }
  }

  function activate(index: number): void {
    active = index;
    Array.from(list.children).forEach((item, itemIndex) => {
      const selected = itemIndex === active;
      item.setAttribute('aria-selected', String(selected));
      if (selected) item.setAttribute('data-active', 'true');
      else item.removeAttribute('data-active');
    });
    input.setAttribute('aria-activedescendant', `${list.id}-${active}`);
    const item = list.children[active] as HTMLElement | undefined;
    item?.scrollIntoView?.({ block: 'nearest' });
  }

  function accept(index: number): void {
    if (!enabled() || !open || !addresses[index]) return;
    selecting = true;
    input.value = addresses[index];
    close();
    input.focus();
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    selecting = false;
  }

  function keydown(event: KeyboardEvent): void {
    if (!enabled() || event.isComposing || event.keyCode === 229) { close(); return; }
    if (event.key === 'Tab') { close(); return; }
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!open) refresh();
      if (!open) return;
      event.preventDefault();
      event.stopPropagation();
      activate(event.key === 'ArrowDown' ? (active + 1) % addresses.length : (active < 0 ? addresses.length - 1 : (active - 1 + addresses.length) % addresses.length));
    } else if (event.key === 'Enter' && open) {
      // A visible manual suggestion list must never accidentally submit the form.
      event.preventDefault();
      event.stopPropagation();
      if (active >= 0) accept(active);
    }
  }

  function pointerdown(event: PointerEvent): void {
    if (!enabled() || (event.button !== 0 && event.button !== -1)) return;
    const item = event.target instanceof Element ? event.target.closest<HTMLElement>('[role="option"]') : null;
    if (!item || !list.contains(item)) return;
    // Mouse clicks keep input focus. Touch remains scrollable and accepts only
    // after a completed tap emits click, never when a scrolling gesture starts.
    if (event.pointerType !== 'touch') event.preventDefault();
  }

  function click(event: MouseEvent): void {
    const item = event.target instanceof Element ? event.target.closest<HTMLElement>('[role="option"]') : null;
    if (item && list.contains(item)) accept(Number(item.dataset.index));
  }

  function compositionStart(): void { composing = true; close(); }
  function compositionEnd(): void { composing = false; refresh(); }
  function blur(event: FocusEvent): void {
    if (!(event.relatedTarget instanceof Node) || !panel.contains(event.relatedTarget)) close();
  }
  const form = input.form;
  input.addEventListener('input', refresh);
  input.addEventListener('focus', refresh);
  input.addEventListener('blur', blur);
  input.addEventListener('keydown', keydown);
  input.addEventListener('compositionstart', compositionStart);
  input.addEventListener('compositionend', compositionEnd);
  panel.addEventListener('pointerdown', pointerdown);
  panel.addEventListener('click', click);
  panel.addEventListener('keydown', keydown);
  form?.addEventListener('reset', close);
  const observer = new MutationObserver(() => { if (!enabled()) close(); });
  // Fieldset disabled state also disables descendant controls; watching the
  // containing form covers that state without changing the native semantics.
  observer.observe(form ?? input, { attributes: true, subtree: true, attributeFilter: ['disabled', 'readonly'] });

  return {
    dispose(): void {
      if (disposed) return;
      close();
      disposed = true;
      observer.disconnect();
      input.removeEventListener('input', refresh);
      input.removeEventListener('focus', refresh);
      input.removeEventListener('blur', blur);
      input.removeEventListener('keydown', keydown);
      input.removeEventListener('compositionstart', compositionStart);
      input.removeEventListener('compositionend', compositionEnd);
      panel.removeEventListener('pointerdown', pointerdown);
      panel.removeEventListener('click', click);
      panel.removeEventListener('keydown', keydown);
      form?.removeEventListener('reset', close);
      popup.dispose();
      panel.remove();
      attributes.forEach((name, index) => {
        const value = previous[index];
        if (value === null) input.removeAttribute(name);
        else input.setAttribute(name, value);
      });
    },
  };
}
